import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RoleBasedNavigation = () => {
  const roleEntryPoints = [
    {
      id: 'executive',
      title: "Executive Brief",
      subtitle: "Strategic AI Leadership",
      description: "C-suite focused insights on AI as competitive advantage, risk mitigation, and organizational transformation strategy.",
      icon: "Crown",
      color: "from-purple-600 to-purple-700",
      features: [
        "ROI analysis and business case",
        "Competitive advantage strategies",
        "Risk assessment frameworks",
        "Executive implementation roadmaps"
      ],
      cta: "View Executive Resources",
      path: "/resources-hub"
    },
    {
      id: 'technical',
      title: "Technical Deep-Dive",
      subtitle: "Engineering Excellence",
      description: "CTO and engineering leader resources for building robust, scalable AI infrastructure with open-source technologies.",
      icon: "Code2",
      color: "from-blue-600 to-blue-700",
      features: [
        "Architecture patterns and best practices",
        "Open-source technology stacks",
        "Implementation methodologies",
        "Performance optimization guides"
      ],
      cta: "Explore Technical Resources",
      path: "/ai-core-blueprint"
    },
    {
      id: 'assessment',
      title: "Quick Assessment",
      subtitle: "Immediate Insights",
      description: "Interactive tools to evaluate your current AI maturity, vendor dependencies, and readiness for transformation.",
      icon: "Target",
      color: "from-green-600 to-green-700",
      features: [
        "AI readiness evaluation",
        "Vendor lock-in risk calculator",
        "Cost-benefit analysis tools",
        "Personalized recommendations"
      ],
      cta: "Start Assessment",
      path: "/get-started"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Choose Your Journey
          </h2>
          <p className="text-xl text-text-secondary">
            Personalized pathways based on your role and immediate needs. Get the right information at the right depth for your decision-making process.
          </p>
        </div>

        {/* Role Cards */}
        <div className="grid lg:grid-cols-3 gap-8">
          {roleEntryPoints?.map((role) => (
            <div key={role?.id} className="group relative">
              <div className="bg-white rounded-2xl shadow-executive hover:shadow-executive-lg transition-all duration-300 overflow-hidden border border-gray-100 group-hover:border-brand-secondary/20">
                {/* Header with Gradient */}
                <div className={`bg-gradient-to-r ${role?.color} p-6 text-white relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                    <Icon name={role?.icon} size={128} />
                  </div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={role?.icon} size={24} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{role?.title}</h3>
                    <p className="text-white/90 font-medium">{role?.subtitle}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  <p className="text-text-secondary leading-relaxed">
                    {role?.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-brand-primary">What you'll find:</h4>
                    <ul className="space-y-2">
                      {role?.features?.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-text-primary">
                          <Icon name="Check" size={16} className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <Link to={role?.path} className="block">
                    <button className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center group-hover:bg-brand-secondary">
                      {role?.cta}
                      <Icon name="ArrowRight" size={16} className="ml-2" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Navigation Options */}
        <div className="mt-16 bg-white rounded-2xl shadow-executive p-8 border border-gray-100">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-brand-primary mb-2">
              Not Sure Where to Start?
            </h3>
            <p className="text-text-secondary">
              Explore our comprehensive resources or speak directly with an AI independence specialist
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/about-ai-clean-core" className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-brand-secondary hover:bg-brand-surface transition-all duration-200 group">
              <Icon name="Info" size={20} className="text-brand-primary mr-3" />
              <div>
                <div className="font-medium text-brand-primary group-hover:text-brand-secondary">About Us</div>
                <div className="text-sm text-text-secondary">Our story & approach</div>
              </div>
            </Link>

            <Link to="/case-studies-proof" className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-brand-secondary hover:bg-brand-surface transition-all duration-200 group">
              <Icon name="FileText" size={20} className="text-brand-primary mr-3" />
              <div>
                <div className="font-medium text-brand-primary group-hover:text-brand-secondary">Case Studies</div>
                <div className="text-sm text-text-secondary">Success stories</div>
              </div>
            </Link>

            <Link to="/services-implementation" className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-brand-secondary hover:bg-brand-surface transition-all duration-200 group">
              <Icon name="Settings" size={20} className="text-brand-primary mr-3" />
              <div>
                <div className="font-medium text-brand-primary group-hover:text-brand-secondary">Services</div>
                <div className="text-sm text-text-secondary">How we help</div>
              </div>
            </Link>

            <Link to="/open-source-advantage" className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-brand-secondary hover:bg-brand-surface transition-all duration-200 group">
              <Icon name="Unlock" size={20} className="text-brand-primary mr-3" />
              <div>
                <div className="font-medium text-brand-primary group-hover:text-brand-secondary">Open Source</div>
                <div className="text-sm text-text-secondary">Why it matters</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoleBasedNavigation;